import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setSubmitted(true);
      toast.success('Password reset instructions sent to your email');
    } catch (err: any) {
      console.error('Error sending reset email:', err);
      setError(err.message || 'Failed to send reset instructions');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative bg-gradient-to-br from-royal-surface via-white to-royal-gold-light/10">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-royal-gold/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-royal-gold-dark/10 rounded-full blur-3xl" />
      </div>

      <button
        onClick={() => navigate('/login')}
        className="absolute top-8 left-8 p-2 hover:bg-royal-gold/10 rounded-xl transition-colors flex items-center space-x-2 text-royal-text-secondary hover:text-royal-text"
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-medium">Back to login</span>
      </button>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {submitted ? (
          <div className="text-center space-y-6 bg-white rounded-3xl shadow-royal p-8 border border-royal-border">
            <div className="w-16 h-16 bg-gradient-to-br from-royal-gold-light to-royal-gold rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-royal-gold/25">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-royal-gold-dark via-royal-gold to-royal-gold-light bg-clip-text text-transparent">
              Check your email
            </h2>
            <p className="text-royal-text-secondary">
              We've sent password reset instructions to <span className="font-bold text-royal-text">{email}</span>
            </p>
            <button
              onClick={() => navigate('/login')}
              className="inline-block font-bold text-royal-gold hover:text-royal-gold-dark transition-colors"
            >
              Return to login
            </button>
          </div>
        ) : (
          <>
            <div className="text-center space-y-3">
              <h2 className="text-4xl font-bold bg-gradient-to-r from-royal-gold-dark via-royal-gold to-royal-gold-light bg-clip-text text-transparent">
                Reset your password
              </h2>
              <p className="text-lg text-royal-text-secondary">
                Enter your email address and we'll send you instructions to reset your password.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 bg-white rounded-3xl shadow-royal p-8 border border-royal-border">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <span className="text-red-700 text-sm font-medium">{error}</span>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-bold text-royal-text mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full px-4 py-3 bg-white border border-royal-border rounded-xl text-royal-text placeholder:text-royal-text-secondary focus:outline-none focus:ring-2 focus:ring-royal-gold/30 focus:border-royal-gold transition-all"
                  placeholder="Enter your email"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-4 px-6 rounded-2xl text-base font-bold text-white bg-gradient-to-r from-royal-gold-dark via-royal-gold to-royal-gold-light hover:shadow-royal-lg focus:outline-none focus:ring-2 focus:ring-royal-gold/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-royal-gold/25"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  'Send reset instructions'
                )}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default ForgotPassword;